/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { range } from "../../../utils/lodash";
import useStyles from "./StarRatingRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import Button from "../../shared/Button";
import useQuestion from "../../../hooks/useQuestion";
import { getAriaLabelledByFromId } from "../../../utils/WCAGHelper";
import { QuestionType } from "../../../models/enums/QuestionType";
import StarRenderer from "./StarRenderer";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { StarRatingNavigation } from "../../controls/KeyboardNavigation/StarRatingNavigation";

interface Props extends RendererProps {
    containerRef: React.LegacyRef<HTMLDivElement>;
    setValue(value: number, reset?: boolean): void;
    starWidth?: number;
    starHeight?: number;
    value: number;
    split: number;
    maxValue: number;
    elementId: string;
    isDisabled?: boolean;
}

function getStars(props: Props) {
    const starCount = props.maxValue * (props.split || 1);
    return range(starCount);
}

export default function StarRatingRenderer(props: Props) {
    const [hoveredStarValue, setHoveredStarValue] = React.useState(0);
    const {
        value,
        setValue,
        elementId,
        split = 1,
        starWidth = 24,
        starHeight = 24,
        isDisabled = false,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
        gridRendering,
        variable,
    } = props;
    const theme = useTheme();
    const question = useQuestion();
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const rerenderParentCallback = (input) => {
        setHoveredStarValue(input);
    };
    const ariaAttributes = {};

    // Star rating is only available in Hybrid grid question i.e. we can use gridRendering
    if (gridRendering) {
        ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
            question,
            questionId,
            `${QuestionType.HybridGrid}_STARRATING`,
            gridRenderedColumnIndex + 1,
            gridRenderedRowIndex + 1,
        );
    }

    const splitWidth = starWidth / split;
    const classes = useStyles({ theme, starWidth, starHeight, splitWidth });

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        `${elementId}_CLEAR`,
        variable,
        question,
        StarRatingNavigation,
        null,
    );

    return (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
            id={elementId}
            className={`${classnames(classes.starRating)} ${isDisabled ? classes.disabled : ""}`}
            onMouseOut={isDisabled ? undefined : () => setHoveredStarValue(0)}
            role="radiogroup"
            {...ariaAttributes}
        >
            <div className="flex" />
            {getStars(props).map((index) => {
                const starValue = (index + 1) / split;

                const spacer = (
                    <div
                        className={`${classnames(classes.starRatingSpacer)} ${
                            isDisabled ? classes.disabled : ""
                        }`}
                        onClick={isDisabled ? undefined : () => setValue(starValue)}
                        onMouseOver={isDisabled ? undefined : () => setHoveredStarValue(starValue)}
                    />
                );

                return (
                    <React.Fragment key={index}>
                        {index % split === 0 && spacer}
                        <StarRenderer
                            index={index}
                            setValue={setValue}
                            value={value}
                            elementId={elementId}
                            split={split}
                            starWidth={starWidth}
                            starHeight={starHeight}
                            isDisabled={isDisabled}
                            variable={variable}
                            rerenderParentCallback={rerenderParentCallback}
                            hoveredStarValue={hoveredStarValue}
                            question={undefined}
                            questionnaire={undefined}
                            theme={undefined}
                            language=""
                        />
                        {index % split === split - 1 && spacer}
                    </React.Fragment>
                );
            })}
            <div className="flex" />
            <Button
                onKeyDownEventHandler={onKeyDown}
                buttonElementRef={elementReference}
                buttonType="CLEARSTARRATING"
                icon={["fas", "times"]}
                size={16}
                onClick={isDisabled ? undefined : () => setValue(null, true)}
                disabled={isDisabled === true}
            />
        </div>
    );
}
